<template>
    <b-modal
        id="delivery-requests"
        size="lg"
        title="Choose service"
        scrollable
        hide-footer
        td-сlass="align-middle"
        th-сlass="hide"
        @shown="getData"
        @hide="hideModal"
    >
        <div v-if="!tableBusy && items.length > 0" class="d-flex justify-content-between mb-2">
            <div><small><b class="text-user-primary">Parcel value:</b> {{items[0].totalValue}} {{items[0].currencyCode}}</small></div>
            <div v-if="preferableDelivery"><small><b class="text-user-primary">Buyer`s chosen delivery type: </b><span v-text="preferableDelivery"></span></small></div>
        </div>
        <div v-if="tableBusy" class="text-center text-info my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>Loading...</strong>
        </div>
        <b-table
            v-else
            show-empty
            outlined
            emptyText="Empty data"
            emptyFilteredText="Empty results"
            :items="items"
            :fields="fields"
        >
            <template slot="logo" slot-scope="data">
                <b-img fluid :src="data.item.serviceImageUrl" class="mx-auto d-block default-service-logo" v-if="serviceImageUrlValidation(data.item.serviceImageUrl)"></b-img>
                <span class="d-block text-center" v-else><b>No logo</b></span>
            </template>
            <template slot="name" slot-scope="data">
                <div>
                    <b v-text="data.item.deliveryName"></b>
                </div>
                <div class="no-wrap">
                    <span>Delivery term:</span>
                    <span>{{data.item.deliveryPeriodFrom}}</span><span v-if="data.item.deliveryPeriodTo && data.item.deliveryPeriodTo !== data.item.deliveryPeriodFrom">-{{data.item.deliveryPeriodTo}}</span><span>&nbsp;(working days)</span>
                </div>
                <template v-if="data.item.allowInsurance">
                    <b-form-checkbox
                        v-model="data.item.insurance"
                        :name="'checkbox-' + data.item.serviceId"
                        class="d-inline-block"
                    >
                        Add insurance,&nbsp;
                    </b-form-checkbox>
                    <div>
                        <span>price {{+data.item.insurancePrice.toFixed(2)}} {{ data.item.currencyCode }}</span>
                        <i class="icon-info cursor-pointer" :id="'tooltip-' + data.item.serviceId"></i>
                        <b-tooltip :target="'tooltip-' + data.item.serviceId" triggers="hover">
                            {{ $t("orders.delivery_modal.cost_tooltip", { fee: data.item.fee, currencyCode: data.item.currencyCode, insurancePrice: minInsurancePrice }) }}
                        </b-tooltip>
                    </div>
                </template>
                <template v-if="showLockerList(data.item.recipientAddressType)">
                    <locker-list
                        :lockerType="data.item.recipientAddressType"
                        :locker-list-parent="selectOptions(data.item.recipientAddressType)"
                        :fetchLockerList="fetchLockerList"
                        @lockerChosen="(payload) => lockerChosen(payload, data)"></locker-list>
                </template>
            </template>
            <template slot="price" slot-scope="data">
                <div class="text-center">
                    <span class="h3 text-user-primary"><b>{{ data.item.currencyCode }} {{data.item.price}}</b></span>
                    <small v-if="data.item.vat > 0" class="text-user-primary">+ VAT</small>
                    <div>({{ data.item.currencyCode }} {{data.item.totalPrice}})</div>
                </div>
            </template>
            <template slot="action" slot-scope="data">
                <button class="btn text-nowrap btn-primary-user pull-right"
                     :disabled="disabledChooseButton(data.item)"
                     @click="showLockers ? sendDelivery(data.item) : getLocker(data.item)"
                >Choose</button>
            </template>
        </b-table>
    </b-modal>
</template>

<script>
    export default {
        name: 'OrderDelivery',
        components: {
            LockerList: () => import('./OrderDeliveryLockerList')
        },
        props: {
            id: {
                type: [Number, String],
                required: true
            },
            preferableDelivery: {
                default: null
            },
            orderAddress: {
                default: null
            },
            showLockers: {
                default: null
            },
            minInsurancePrice: {
                type: Number
            }
        },
        data() {
            return {
                fields: [
                    { key: 'logo', label: '', thClass: 'd-none', tdClass: 'align-middle w-25' },
                    { key: 'name', label: '', thClass: 'd-none', tdClass: 'align-middle' },
                    { key: 'price', label: '', thClass: 'd-none', tdClass: 'align-middle w-30' },
                    { key: 'action', label: '', thClass: 'd-none', tdClass: 'align-middle' }
                ],
                items: [],
                tableBusy: true,
                omnivaLocker: null,
                dpdLocker: null
            }
        },
        computed: {
            disabledChooseButton() {
                return (item) => {
                    let locker = this.selectOptions(item.recipientAddressType)
                    return item.recipientAddressType !== '' && this.showLockers && (locker && locker.items.length > 0 && !item.lockerChosen)
                }
            },
            showLockerList() {
                return (recipientAddressType) => {
                    let locker = this.selectOptions(recipientAddressType)
                    return !!recipientAddressType && this.showLockers && (locker && locker.items.length > 0)
                }
            },
            selectOptions() {
                return (type) => {
                    let options = null
                    switch (!!type) {
                        case type === 'OMNIVA':
                            options = this.omnivaLocker
                            break;
                        case type === 'DPD':
                            options = this.dpdLocker
                            break;
                    }
                    return options
                }
            },
        },
        methods: {
            serviceImageUrlValidation(url) {
                return url.split('/').slice(3).length > 1
            },
            getData() {
                this.$api('GET', this.$endpoints.orders.deliveryOffers(this.id)).then(async (response) => {
                    this.items = response
                    this.tableBusy = false
                    if (!this.showLockers) return false
                    this.omnivaLocker = await this.fetchLockerList('OMNIVA')
                    this.dpdLocker = await this.fetchLockerList('DPD')
                }).catch((error) => {
                    this.tableBusy = false
                })
            },
            resetModal() {
                this.tableBusy = true
                this.items = []
            },
            hideModal() {
                this.resetModal()
                this.$emit('hide')
            },
            getLocker(item) {
                this.tableBusy = true

                let lockerQuery = 'zip=' + this.orderAddress.zip +
                    '&address=' + this.orderAddress.address +
                    '&terminalType=' + item.recipientAddressType +
                    '&city=' + this.orderAddress.cityName +
                    '&country=' + this.orderAddress.countryId

                if (item.recipientAddressType) {
                    // Check locker list
                    this.$api('GET', this.$endpoints.orders.getDeliveryLocker(lockerQuery)).then((response) => {
                        if (response) {
                            if (response.id) {
                                this.sendDelivery(item, response.id)
                            } else if (response[0].id) {
                                this.sendDelivery(item, response[0].id)
                            }
                        }
                    }).catch(() => {
                        this.tableBusy = false
                    })
                } else {
                    this.sendDelivery(item)
                }
            },
            fetchLockerList(type, params, actionType) {
                if (!this.orderAddress) return false
                let lockerQuery = {
                    'filter[country]': +this.orderAddress.countryId,
                    'filter[terminalType]': type
                }

                if (actionType === 'search') {
                    delete lockerQuery['filter[city]']
                }

                return this.$api('GET', this.$endpoints.orders.getDeliveryLockerList(), null, Object.assign(lockerQuery, params))
                    .catch(ex => console.error(ex))
                    .finally(() => {})
            },
            sendDelivery(item, lockerId = null) {
                if (item.lockerChosen) {
                    lockerId = item.lockerChosen.id
                }
                this.tableBusy = true

                let query = 'serviceCode=' + item.serviceId + '&companyName=' + item.deliveryName + '&lockerId=' + lockerId
                if (item.insurance) {
                    query += '&insurancePrice=' + item.insurancePrice
                }

                this.$api('POST', this.$endpoints.orders.applyDelivery(this.id, query)).then((response) => {
                    if (response) {
                        this.$api('POST', this.$endpoints.orders.fulfillment(this.id)).then(() => {
                            this.$store.dispatch('globalAlertAction', {
                                type: 'success',
                                title: `Order-${this.id} sent to fulfillment.`,
                            })
                        }).then((response) => {
                            this.resetModal()
                            this.$root.$emit('bv::hide::modal', 'delivery-requests')
                            this.$emit('updateList')
                        })
                    }
                }).catch(() => {
                    this.tableBusy = false
                })
            },
            lockerChosen(payload, data) {
                let updateItem = Object.assign({}, data.item)
                updateItem.lockerChosen = payload
                this.$set(this.items, data.index, updateItem)
            }
        }
    }
</script>



// WEBPACK FOOTER //
// src/components/UserPanel/Pages/Orders/OrderDelivery.vue